import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { ExternalLink, Menu, X, Lock } from "lucide-react";

const EMPLOYEE_PORTAL = "https://hma.snawbar.net/dashboard";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Data Sheets" },
  { to: "/projects", label: "Projects" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        scrolled ? "bg-slate-50/85 backdrop-blur-xl border-slate-200/80" : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[88px] flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                pathname === l.to ? "text-brand-400" : "text-slate-600 hover:text-slate-900 hover:bg-white/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/admin"
            data-testid="nav-admin"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white/5 transition-colors"
          >
            <Lock size={14} /> Admin
          </Link>
          <a
            href={EMPLOYEE_PORTAL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="employee-portal-header"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-brand-500/40 bg-brand-500/10 text-brand-400 hover:bg-brand-500/20 transition-colors"
          >
            Employee Portal <ExternalLink size={14} />
          </a>
        </div>

        <button
          className="lg:hidden text-slate-900 p-2"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-slate-50/95 backdrop-blur-xl border-t border-slate-200 px-5 py-4 flex flex-col gap-1" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-4 py-3 rounded-md text-sm font-medium ${
                pathname === l.to ? "text-brand-400 bg-white/5" : "text-slate-700"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <ThemeToggle className="mt-2" />
          <Link
            to="/admin"
            data-testid="nav-admin-mobile"
            className="px-4 py-3 rounded-md text-sm font-medium text-slate-700 inline-flex items-center gap-2"
          >
            <Lock size={14} /> Admin
          </Link>
          <a
            href={EMPLOYEE_PORTAL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="employee-portal-mobile"
            className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold border border-brand-500/40 bg-brand-500/10 text-brand-400"
          >
            Employee Portal <ExternalLink size={14} />
          </a>
        </div>
      )}
    </header>
  );
};
